import { useRef, useState, type InputHTMLAttributes } from 'react';
import { Popover } from 'radix-ui';
import { CalendarDays, Clock } from 'lucide-react';
import { parse } from 'date-fns';
import { DATE_FORMATS, HM, YMD, formatDate, formatTime, parseDate, parseTime, useFormats } from '@/lib/formats';
import { FieldButton, Input } from './Field';
import { ClockDial } from './ClockDial';
import { cn } from '@/lib/cn';

type Common = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'placeholder'> & { value: string; onChange: (canonical: string) => void; /** For the <input> itself; `className` sizes the wrapper. */ inputClassName?: string };

/**
 * A text field that shows and accepts the person's own time format while the app keeps "HH:mm" (Settings ›
 * Appearance). The browser's <input type="time"> cannot be told which format to use, so it is not used. Only a
 * focused field keeps a draft; unfocused ones always show the formatted value, so a field filled by code updates.
 */
/** `suggest` ("HH:mm") is shown greyed while the field is empty and is where the dial opens; it is never a value. */
export function TimeField({ value, onChange, suggest, className, inputClassName, id, ...props }: Common & { suggest?: string }) {
  const { timeFormat } = useFormats();
  const [draft, setDraft] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const shown = draft ?? (value ? formatTime(parse(value, HM, new Date()), timeFormat) : '');
  const commit = (text: string) => { const c = parseTime(text, timeFormat); if (c !== null || text.trim() === '') onChange(c ?? ''); };
  return (
    <div className={cn('relative w-[112px] shrink-0', className)}>
      <Input {...props} id={id} mono inputMode="numeric" autoComplete="off" placeholder={formatTime(parse(suggest ?? '09:00', HM, new Date()), timeFormat)} value={shown}
        onFocus={() => setDraft(shown)} onBlur={() => setDraft(null)}
        onChange={(e) => { setDraft(e.target.value); commit(e.target.value); }} className={cn('w-full pr-9', inputClassName)} />
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <FieldButton aria-label="Pick a time"><Clock size={15} /></FieldButton>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content align="end" sideOffset={6} className="z-50 rounded-input border border-chip-border bg-canvas shadow-popover p-4 outline-none">
            <ClockDial value={value || suggest || '09:00'} timeFormat={timeFormat} onChange={onChange} onDone={() => setOpen(false)} />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}

/**
 * A date typed in the person's format, with the browser's calendar one click away for picking. The calendar's
 * own labels follow the browser, but what lands in the field follows the setting, and the app keeps "yyyy-MM-dd".
 */
export function DateField({ value, onChange, className, inputClassName, id, ...props }: Common) {
  const { dateFormat } = useFormats();
  const [draft, setDraft] = useState<string | null>(null);
  const native = useRef<HTMLInputElement>(null);
  const shown = draft ?? (value ? formatDate(parse(value, YMD, new Date()), dateFormat) : '');
  const commit = (text: string) => { const c = parseDate(text, dateFormat); if (c !== null) onChange(c); };
  const openPicker = () => { const el = native.current; if (!el) return; if ('showPicker' in el) { try { (el as HTMLInputElement & { showPicker: () => void }).showPicker(); return; } catch { /* needs a user gesture; fall through */ } } el.click(); };
  return (
    <div className={cn('relative', className)}>
      <Input {...props} id={id} mono inputMode="numeric" autoComplete="off" placeholder={DATE_FORMATS[dateFormat].placeholder} value={shown}
        onFocus={() => setDraft(shown)} onBlur={() => setDraft(null)}
        onChange={(e) => { setDraft(e.target.value); commit(e.target.value); }} className={cn('w-full pr-9', inputClassName)} />
      <FieldButton onClick={openPicker} aria-label="Pick a date"><CalendarDays size={15} /></FieldButton>
      {/* The picker only: visually gone, still in the tree so showPicker() has an input to open. */}
      <input ref={native} type="date" tabIndex={-1} aria-hidden="true" value={value} onChange={(e) => e.target.value && onChange(e.target.value)} className="absolute inset-0 w-0 h-0 opacity-0 pointer-events-none" />
    </div>
  );
}
